import { AppRouteRecordRaw } from '../types';
import { PARTNER_TYPES } from '@/constants/partner-types';
import { Roles } from '@/constants/roles';

const Body = () => import('@/components/body.vue');
const OrderSearchByPoiWrapper = () =>
  import('@/pages/troubleshooting/order-search/OrderSearchByPoiWrapper.vue');
const InsightsAndAnalyticsOverview = () =>
  import('@/pages/insights-and-analytics/overview/InsightsAndAnalyticsOverview.vue');
const OrderSearchInvidualPartner = () =>
  import('@/pages/insights-and-analytics/orders/OrderSearchInvidualPartner.vue');
const OrderListApiPartner = () =>
  import('@/pages/insights-and-analytics/orders/OrderListApiPartner.vue');
const PartnerInteractionLog = () =>
  import('@/pages/individual-partners/partner-interaction-log/PartnerInteractionLog.vue');
const TransactionExchangeLog = () =>
  import('@/pages/insights-and-analytics/transaction-data-log/TransactionExchangeLog.vue');
const TransactionExchangeLogOrder = () =>
  import('@/pages/insights-and-analytics/transaction-data-log/TransactionExchangeLogOrder.vue');
const ProductMetadataUnified = () =>
  import('@/pages/insights-and-analytics/product-metadata/ProductMetadataUnified.vue');
const ProductMetadataConcepts = () =>
  import('@/pages/insights-and-analytics/product-metadata/ProductMetadataConcepts.vue');
const StationBrands = () =>
  import('@/pages/insights-and-analytics/station-management/StationBrands.vue');
const CarWashStations = () =>
  import('@/pages/insights-and-analytics/station-management/car-wash-stations/CarWashStations.vue');
const ActiveStations = () =>
  import('@/pages/insights-and-analytics/station-management/fuel-stations/ActiveStations.vue');
const PoiStatistics = () =>
  import('@/pages/insights-and-analytics/station-management/poi-statistics/PoiStatistics.vue');
const AllguthFailedOrders = () =>
  import('@/pages/insights-and-analytics/orders/AllguthFailedOrders.vue');
const PartnerAlertsList = () =>
  import('@/pages/insights-and-analytics/monitoring/partner-alerts/partner-alerts-list/PartnerAlertsList.vue');
const PartnerAlertDetail = () =>
  import('@/pages/insights-and-analytics/monitoring/partner-alerts/partner-alert-details/PartnerAlertDetail.vue');
const ConnectTrackingPartner = () =>
  import('@/pages/insights-and-analytics/monitoring/connect-tracking/ConnectTrackingPartner.vue');
const FailureAnalysisReports = () =>
  import('@/pages/insights-and-analytics/reports/FailureAnalysisReports.vue');
const RydInternalOrdersSearchPage = () =>
  import('@/pages/insights-and-analytics/orders/ryd-internal-search/RydInternalOrdersSearchPage.vue');

export const insightsAndAnalyticsRoutes: AppRouteRecordRaw[] = [
  {
    path: '/insights-and-analytics',
    component: Body,
    meta: {
      breadcrumb: 'Insights & Analytics',
    },
    children: [
      {
        path: 'overview',
        name: 'insights-and-analytics-overview',
        component: InsightsAndAnalyticsOverview,
        meta: {
          title: ' Insights & Analytics Overview | ryd Portal',
          breadcrumb: 'Overview',
        },
      },
      {
        path: 'order-search-by-location',
        name: 'order-search-by-location',
        component: OrderSearchByPoiWrapper,
        meta: {
          title: ' Order Search By Location | ryd Portal',
          breadcrumb: 'Order Search By Location',
        },
      },
      {
        path: 'ip/order-search',
        name: 'individual-partner-order-search',
        component: OrderSearchInvidualPartner,
        meta: {
          title: ' Order Search Individual Partner | ryd Portal',
          breadcrumb: 'Order Search Bosch',
        },
      },
      {
        path: 'api/order-list',
        name: 'api-partner-order-list',
        component: OrderListApiPartner,
        meta: {
          title: ' Order List API Partner | ryd Portal',
          breadcrumb: 'Order Search',
        },
      },
      {
        path: 'webhook-monitoring',
        name: 'webhook-monitoring',
        redirect: { name: 'WebhookMonitoring' },
        meta: {
          title: 'Webhook Monitoring | ryd Portal',
          breadcrumb: 'Webhook Monitoring',
        },
      },
      {
        // Partner-facing alerts surface. Gated to userGroup=mobiko (the only
        // partner currently onboarded). The backend additionally scopes the
        // BigQuery query by the resolved partner_id (see
        // rp-integrations/src/modules/partner-alerts/partner-alerts.access.ts).
        path: 'monitoring/partner-alerts',
        name: 'PartnerAlertsList',
        component: PartnerAlertsList,
        meta: {
          title: 'Partner Alerts | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['mobiko'],
          breadcrumb: 'Partner Alerts',
        },
      },
      {
        path: 'monitoring/partner-alerts/detail',
        name: 'PartnerAlertDetail',
        component: PartnerAlertDetail,
        meta: {
          title: 'Partner Alert Detail | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['mobiko'],
          breadcrumb: 'Partner Alert Detail',
        },
      },
      {
        // Partner-facing ryd Connect tracking. Gated to fleet-API partners; the
        // backend additionally scopes the journey to the caller's resolved
        // sponsor ids and rejects cross-partner access (see
        // rp-integrations/src/modules/connect-tracking/connect-tracking.access.ts).
        path: 'monitoring/connect-tracking',
        name: 'ConnectTrackingPartner',
        component: ConnectTrackingPartner,
        meta: {
          title: 'ryd Connect Tracking | ryd Portal',
          requiresAuth: true,
          requiredPartnerTypes: [PARTNER_TYPES.API_PARTNER, PARTNER_TYPES.FLEET_PARTNER],
          breadcrumb: 'ryd Connect Tracking',
        },
      },
      {
        // Internal: the partner is chosen on the page, so partners must not reach it.
        path: 'partner-interaction-log',
        name: 'partner-interaction-log',
        component: PartnerInteractionLog,
        meta: {
          title: 'Partner Interaction Log | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Partner Interaction Log',
        },
      },
      {
        // Former Pairpoint-only URL; kept so existing links still land on the log.
        path: 'pairpoint/transaction-exchange-log',
        redirect: (to) => ({
          name: 'partner-interaction-log',
          query: { partner: 'pairpoint', ...to.query },
        }),
      },
      {
        path: 'transaction-exchange-log',
        name: 'transaction-exchange-log',
        component: TransactionExchangeLog,
        meta: {
          title: ' Transaction Exchange Logs | ryd Portal',
          breadcrumb: 'Transaction Exchange Log',
        },
      },
      {
        path: 'transaction-exchange-log-by-order',
        name: 'transaction-exchange-log-by-order',
        component: TransactionExchangeLogOrder,
        meta: {
          title: ' Transaction Exchange Log by Order | ryd Portal',
          breadcrumb: 'Transaction Exchange Log for Order',
        },
      },
      {
        path: 'product-metadata',
        name: 'product-metadata-unified',
        component: ProductMetadataUnified,
        meta: {
          title: ' Product Metadata | ryd Portal',
          breadcrumb: 'Product Metadata',
        },
      },
      {
        path: 'product-metadata/concepts',
        name: 'product-metadata-concepts',
        component: ProductMetadataConcepts,
        meta: {
          title: ' Product Metadata Concepts | ryd Portal',
          breadcrumb: 'Product Metadata Concepts',
        },
      },
      {
        path: 'station-management/brands',
        name: 'station-brands',
        component: StationBrands,
        meta: {
          title: ' Station Brands | ryd Portal',
          breadcrumb: 'Station Brands',
        },
      },
      {
        path: 'station-management/car-wash-stations',
        name: 'car-wash-stations',
        component: CarWashStations,
        meta: {
          title: ' Car Wash Stations | ryd Portal',
          breadcrumb: 'Car Wash Stations',
        },
      },
      {
        // Re-registered after the export moved off partner-performance-guardian onto
        // rp-integrations. Previously reachable at /dashboard/active-stations; it now sits
        // with its Station Management siblings. Gated on the same partner types as the menu
        // entry, and rp-integrations repeats the check because the path is guessable.
        path: 'station-management/petrol-stations',
        name: 'petrol-stations',
        component: ActiveStations,
        meta: {
          title: ' Petrol Stations Download | ryd Portal',
          breadcrumb: 'Petrol Stations Download',
          requiredPartnerTypes: [
            PARTNER_TYPES.RYD_INTERNAL,
            PARTNER_TYPES.API_PARTNER,
            PARTNER_TYPES.INDIVIDUAL_PARTNER,
          ],
        },
      },
      {
        // ryd-internal ingestion workspace. Gated on userGroup=ryd AND the partnerAdmin role —
        // the same pair the menu entry uses, because hiding a menu item is not a permission:
        // the path is guessable and the route is what actually refuses. The backend
        // independently requires operations_admin to publish a rejected run.
        path: 'station-management/poi-statistics',
        name: 'poi-statistics',
        component: PoiStatistics,
        meta: {
          title: 'POI Statistics Ingestion | ryd Portal',
          headTitle: 'POI Statistics Ingestion',
          requiredUserGroup: ['ryd'],
          requiredRoles: [Roles.partnerAdmin],
          breadcrumb: 'POI Statistics Ingestion',
        },
      },
      {
        // Failure analysis reports for fleetcor. Gated to userGroup=fleetcor;
        // the backend additionally scopes reports by the caller's userGroup via
        // the `accessibleTo` filter (see rp-integrations partner-reports.service).
        path: 'failure-analysis-reports',
        name: 'failure-analysis-reports',
        component: FailureAnalysisReports,
        meta: {
          title: 'Failure Analysis Reports | ryd Portal',
          requiresAuth: true,
          requiredUserGroup: ['fleetcor'],
          breadcrumb: 'Failure Analysis Reports',
        },
      },
      {
        path: 'orders/allguth-failed-orders',
        name: 'allguth-failed-orders',
        component: AllguthFailedOrders,
        meta: {
          title: 'Allguth Fehlgeschlagene Transaktionen | ryd Portal',
          breadcrumb: 'Allguth Fehlgeschlagene Transaktionen',
        },
      },
      {
        path: 'orders/ryd-internal-search',
        name: 'ryd-internal-orders-search',
        component: RydInternalOrdersSearchPage,
        meta: {
          title: 'Internal Orders Search | ryd Portal',
          requiresAuth: true,
          requiredPartnerTypes: [PARTNER_TYPES.RYD_INTERNAL],
          requiredUserGroup: ['ryd'],
          breadcrumb: 'Internal Orders Search',
        },
      },
    ],
  },
];
